import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/+$/, "") || "";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const isDark = localStorage.getItem("theme") === "dark";

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDark);
    document.body.classList.toggle("dark", isDark);
  }, [isDark]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const disabled = useMemo(() => {
    return loading || !email.trim() || !password.trim();
  }, [email, password, loading]);

  async function handleLogin(e) {
    e.preventDefault();

    if (loading) return;

    setError("");

    const cleanEmail = email.trim();
    const cleanPassword = password.trim();

    if (!cleanEmail || !cleanPassword) {
      setError(t("login.errors.required"));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: cleanEmail,
          password: cleanPassword,
        }),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = null;

      if (contentType.includes("application/json")) {
        data = await res.json().catch(() => null);
      } else {
        data = await res.text().catch(() => "");
      }

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error(t("login.errors.invalidCredentials"));
        }

        if (res.status === 403) {
          const msg403 = (typeof data === "object" ? data?.message : data) ?? "";
          if (/suspend/i.test(msg403)) {
            throw new Error(t("login.errors.suspended"));
          }
          throw new Error(t("login.errors.blocked"));
        }

        const backendMessage =
          typeof data === "object" && data?.message
            ? data.message
            : typeof data === "string" && data.trim()
            ? data.trim()
            : "";

        throw new Error(backendMessage || t("common.serverError", { status: res.status }));
      }

      if (!data || typeof data !== "object") {
        throw new Error(t("login.errors.badResponse"));
      }

      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("agro:loginAt", String(Date.now()));
      const from = searchParams.get("from");
      navigate(from && from.startsWith("/") ? from : "/main", { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(err?.message || t("login.errors.noConnection"));
    } finally {
      setLoading(false);
    }
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    if (error) setError("");
  }

  function handlePasswordChange(e) {
    setPassword(e.target.value);
    if (error) setError("");
  }

  return (
    <div className="page">
      <div className="card" style={{ padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: "var(--icon-bg)",
            display: "grid",
            placeItems: "center",
            fontWeight: 950,
            fontSize: 20,
            color: "var(--icon-text)",
            flexShrink: 0,
          }}>A</div>
          <div>
            <h1 className="title" style={{ fontSize: 22 }}>AgroVision</h1>
            <p className="subtitle" style={{ margin: "2px 0 0" }}>{t("login.title")}</p>
          </div>
        </div>

        <form onSubmit={handleLogin} className="form" style={{ marginTop: 20, gap: 12 }}>
          <label className="label" htmlFor="email">
            {t("login.email")}
          </label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Email"
            autoComplete="email"
            required
            style={{ fontSize: 16, minHeight: 50 }}
          />

          <label className="label" htmlFor="password">
            {t("login.password")}
          </label>
          <div style={{ position: "relative" }}>
            <input
              id="password"
              className="input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              style={{ fontSize: 16, minHeight: 50, paddingRight: 52 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              disabled={loading}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 38,
                height: 38,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 16,
                color: "var(--muted)",
                display: "grid",
                placeItems: "center",
              }}
              aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>

          {error ? <div className="error">{error}</div> : null}

          <button
            type="submit"
            className="btn primary"
            disabled={disabled}
            style={{ minHeight: 50, fontSize: 16, marginTop: 4 }}
          >
            {loading ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
